import axios from 'axios';
import localforage from 'localforage';
import { decodeToken, encodeToken } from '@/utils/tokenUtils';
import { performLogout } from '@/services/logoutService';
import { getEnv } from '@/utils/env';

const baseUrl = getEnv('VITE_API_BASEPATH', '');

export class TokenService {
    private static isRefreshing = false;
    private static refreshPromise: Promise<string | null> | null = null;

    private static readonly MAX_REFRESH_RETRIES = 5;
    private static readonly INITIAL_BACKOFF_MS = 400;

    private static delay(ms: number) {
        return new Promise<void>((resolve) => setTimeout(resolve, ms));
    }

    static async refreshToken(): Promise<string | null> {
        if (this.isRefreshing) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;

        this.refreshPromise = (async () => {
            let attempt = 0;

            try {
                const loginToken = (await localforage.getItem('login_token')) as string;
                if (!loginToken) {
                    console.warn('No login token found. Cannot refresh.');
                    return null;
                }

                const decodedToken = decodeToken(loginToken);

                while (attempt < this.MAX_REFRESH_RETRIES) {
                    try {
                        const response = await axios.post(`${baseUrl}/refresh`, null, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${decodedToken}`,
                            },
                        });

                        if (response.data?.status === 400 && response.data?.data?.response === 'current_token_valid') {
                            return loginToken;
                        }

                        if (
                            response.data?.data?.response === 'expired_password' ||
                            response.data?.data?.response === 'invalid_token_provided1'
                        ) {
                            await performLogout();
                            return null;
                        }

                        const newLoginToken = response?.data?.data?.new_token;
                        const tokenExpires = response?.data?.data?.token_expires;

                        if (newLoginToken && tokenExpires) {
                            const encodedToken = encodeToken(newLoginToken);

                            await localforage.setItem('login_token', encodedToken);
                            localStorage.setItem('tokenExpires', tokenExpires.toString());

                            const existingUserDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
                            const updatedUserDetails = {
                                ...existingUserDetails,
                                token: newLoginToken,
                                token_expires: tokenExpires,
                            };
                            localStorage.setItem('userDetails', JSON.stringify(updatedUserDetails));

                            const corporationId = await localforage.getItem('corporation_id');
                            const validCorporationId = typeof corporationId === 'number' ? corporationId : 0;

                            const {
                                data: { data },
                            } = await axios.post(
                                `${baseUrl}/corporate/login`,
                                { corporate_id: validCorporationId },
                                {
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${newLoginToken}`,
                                    },
                                },
                            );

                            const encodeCorpToken = encodeToken(data.token);
                            localStorage.setItem('corporationDetails', JSON.stringify(data));
                            await localforage.setItem('corporate_token', encodeCorpToken);

                            const existingCorporateDetails = JSON.parse(
                                localStorage.getItem('corporationDetails') || '{}',
                            );
                            const hasMultiFlag = localStorage.getItem('hasMultiCorporation') === 'true';
                            const updatedCorporateDetails = {
                                ...existingCorporateDetails,
                                token_expires: tokenExpires,
                            };
                            localStorage.setItem('corporationDetails', JSON.stringify(updatedCorporateDetails));
                            localStorage.setItem('hasMultiCorporation', String(hasMultiFlag));

                            return encodedToken;
                        }

                        console.warn('Token refresh response invalid:', response.data);
                        attempt++;
                    } catch (err: any) {
                        const status = err?.response?.status;

                        if (status === 400 || !err?.response) {
                            attempt++;
                        } else {
                            throw err;
                        }
                    }

                    if (attempt < this.MAX_REFRESH_RETRIES) {
                        const wait = this.INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1);
                        await this.delay(wait);
                    }
                }

                console.error(`Refresh token failed after ${this.MAX_REFRESH_RETRIES} attempts.`);
                await performLogout();
                return null;
            } catch (finalErr) {
                console.error('Failed to refresh token:', finalErr);
                return null;
            } finally {
                this.isRefreshing = false;
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }
}
